import { Fragment, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getRegisteredHotkeys } from "@btnet/core-components/Hotkey/hotkey.registry";
import { transposeKey } from "@btnet/core-components/Hotkey/hotkey.transpose";
import { useHotkey } from "@btnet/core-components/Hotkey/useHotkey";

import { type ITrackingProp, useTracking } from "utilities/analytics/analytics";

import "./HotkeyDisplay.less";

interface IHotkeyDisplayProps extends ITrackingProp {
    message?: string;
}

/**
 * A hotkey to list in the overlay, from either registry. `key` is always OS-transposed
 * (what BDS stores); `el` is only present for BDS keys bound to an element, which are the
 * ones that also get a positioned tooltip.
 */
interface IOverlayHotkey {
    key: string;
    desc: string;
}

function getOverlayHotkeys(): IOverlayHotkey[] {
    const byKey = new Map<string, IOverlayHotkey>();

    getRegisteredHotkeys().forEach(({ command, friendlyName }) => {
        transposeKey(command, "os")
            .split(", ")
            .filter(Boolean)
            .forEach((key) => {
                if (!byKey.has(key)) {
                    byKey.set(key, { key, desc: friendlyName });
                }
            });
    });

    return Array.from(byKey.values());
}

export const HotkeyDisplay = (props: IHotkeyDisplayProps) => {
    const { Track } = useTracking({ component: "HotkeyDisplay" });
    const ref = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);
    const [transition, setTransition] = useState(false);

    const open = () => {
        setTransition(true);
        setVisible(true);
    };
    const close = () => {
        setTransition(false);
        const el = ref.current as HTMLDivElement | undefined;
        const afterTransition = () => {
            el?.removeEventListener("animationend", afterTransition);
            setVisible(false);
        };
        el?.addEventListener("animationend", afterTransition);
    };

    const mapKeys = (key: string) => {
        return (
            <span className="hotkey-display--keygroup">
                {transposeKey(key, "user")
                    .split("+")
                    .map((s: string, i: number, list: string[]) => (
                        <Fragment key={`key-${i}`}>
                            <span className="hotkey-display--key">{s}</span>
                            {i < list.length - 1 ? "+" : ""}
                        </Fragment>
                    ))}
            </span>
        );
    };
    const [list] = useMemo(() => {
        const list: JSX.Element[] = [];

        getOverlayHotkeys().forEach((item: IOverlayHotkey, index: number) => {
            list.push(
                <li key={index}>
                    <span className="hotkey-display--desc">{item.desc}</span>
                    {mapKeys(item.key)}
                </li>,
            );
        });
        return [list];
        // Recompute only when the overlay opens/closes: the registries are read imperatively
        // (a fresh array every call), and tooltip positions must be measured against the live
        // DOM at open time.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useHotkey({
        command: "ctrl+/",
        friendlyName: visible ? "Hide Hotkeys" : "Show Hotkeys",
        onCommand: visible ? close : open,
        triggerBehavior: visible ? "up" : "press",
        ignoreFocusContext: true,
    });

    return createPortal(
        <Track>
            <div>
                {visible && (
                    <div
                        className={`hotkey-display ${transition ? "hotkey-display--visible" : ""}`}
                        onClick={close}
                    >
                        <div ref={ref} className="hotkey-display--legend">
                            <h3>Hotkeys</h3>
                            <div className="hotkey-display--message">{props.message}</div>
                            <ul className="hotkey-display--hotkeys">{list}</ul>
                        </div>
                    </div>
                )}
            </div>
        </Track>,
        document.body,
    );
};
